import { T } from 'gt-react';
import { BellIcon } from '@phosphor-icons/react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useNotificationsList,
  useMarkNotificationAsRead,
  useArchiveNotification,
} from '@/api/rpc/hooks/communications/useNotifications';
import { NotificationItem } from './NotificationItem';

export function NotificationList() {
  const { data, isLoading } = useNotificationsList({
    limit: 20,
    unreadOnly: false,
    filter: 'active',
  });
  const markAsRead = useMarkNotificationAsRead();
  const archive = useArchiveNotification();

  if (isLoading) {
    return (
      <div className="space-y-2 p-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  const notifications = data?.notifications ?? [];

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
        <BellIcon className="h-8 w-8 text-muted-foreground" weight="regular" />
        <div className="text-sm font-medium">
          <T context="empty state title for notification inbox">
            All caught up
          </T>
        </div>
        <div className="text-xs text-muted-foreground">
          <T context="empty state body for notification inbox">
            You have no new notifications.
          </T>
        </div>
      </div>
    );
  }

  return (
    // Native overflow-y-auto over Radix ScrollArea: ScrollArea's viewport
    // doesn't always capture wheel events inside a Popover, letting the
    // page scroll instead. `overscroll-contain` then stops the wheel from
    // bubbling to the body once the popover hits its scroll edges.
    <div className="max-h-[420px] overflow-y-auto overscroll-contain">
      <div className="divide-y">
        {notifications.map((n) => (
          <NotificationItem
            key={n.id}
            notification={n}
            onMarkAsRead={(id) => markAsRead.mutate({ notificationId: id })}
            onArchive={(id) => archive.mutate({ notificationId: id })}
          />
        ))}
      </div>
    </div>
  );
}
